import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../app/store';
import { patchFoodstuff, saveFoodstuff } from '../../state/meals/mealSlice';

import { MeasurementUnit } from '../../models/MeasurementUnit';
import {
  stringToLanguageCode,
  stringsToLanguageCodes,
  globalLanguageCodeToFoodstuff,
} from '../common/LanguageCodeMapping';
import { LanguageCode } from '../common/Language';
import { LanguageSelector } from '../common/LanguageSelector';
import { Density } from '../../models/Density';

function isValidDecimalInput(value: string) {
  return /^\d*\.?\d*$/.test(value);
}

export function AddFoodstuff() {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();

  // Redux selectors
  const foodstuff = useSelector((state: RootState) => state.meal.foodstuff.foodstuff);
  const rawUserLanguages = useSelector(
    (state: RootState) => state.user.user.languageCodes || []
  );
  const userLanguages: LanguageCode[] = useMemo(
    () => stringsToLanguageCodes(rawUserLanguages),
    [rawUserLanguages]
  );

  const [nameInput, setNameInput] = useState('');
  const [baseMeasurementUnit, setBaseMeasurementUnit] = useState<'MASS' | 'VOLUME'>('MASS');

  const [energyInput, setEnergyInput] = useState('');
  const [proteinInput, setProteinInput] = useState('');
  const [carbohydrateInput, setCarbohydrateInput] = useState('');
  const [fatInput, setFatInput] = useState('');
  const [fibreInput, setFibreInput] = useState('');
  const [densityInput, setDensityInput] = useState('');

  const [selectedMeasurementUnits, setSelectedMeasurementUnits] = useState<string[]>([]);

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(() =>
    userLanguages.length > 0 ? userLanguages[0] : LanguageCode.CS
  );

  const measurementUnits: MeasurementUnit[] = useMemo(
    () => [
      { name: 'MILLILITER', symbol: 'ml', conversionFactor: 0, order: 0, selected: false },
      { name: 'LITER', symbol: 'l', conversionFactor: 0, order: 0, selected: false },
      { name: 'TEASPOON', symbol: 'tsp', conversionFactor: 0, order: 0, selected: false },
      { name: 'KILOGRAM', symbol: 'kg', conversionFactor: 0, order: 0, selected: false },
      { name: 'TABLESPOON', symbol: 'tbsp', conversionFactor: 0, order: 0, selected: false },
    ],
    []
  );

  const MenuProps = useMemo(
    () => ({
      PaperProps: {
        style: {
          maxHeight: 200 * 4.5 + 20,
          width: 250,
        },
      },
    }),
    []
  );

  const baseUnitSymbol = baseMeasurementUnit === 'MASS' ? 'g' : 'ml';

  useEffect(() => {
    setNameInput(foodstuff.name || '');
    const hasVolumeDensity = (foodstuff.densities ?? []).some((d) => d.type === 'VOLUME');
    setBaseMeasurementUnit(hasVolumeDensity ? 'VOLUME' : 'MASS');
    const energyVal = foodstuff.energyInKcal ? (foodstuff.energyInKcal * 100).toString() : '';
    setEnergyInput(energyVal);
    setProteinInput(
      foodstuff.proteinMassFraction ? (foodstuff.proteinMassFraction * 100).toString() : ''
    );
    setCarbohydrateInput(
      foodstuff.carbohydrateMassFraction ? (foodstuff.carbohydrateMassFraction * 100).toString() : ''
    );
    setFatInput(
      foodstuff.fatMassFraction ? (foodstuff.fatMassFraction * 100).toString() : ''
    );
    setFibreInput(
      foodstuff.fibreMassFraction ? (foodstuff.fibreMassFraction * 100).toString() : ''
    );

    const volumeDensity = (foodstuff.densities ?? []).find((d) => d.type === 'VOLUME');
    setDensityInput(volumeDensity && volumeDensity.value ? volumeDensity.value.toString() : '');

    const existingUnits = foodstuff.measurementUnits ?? [];
    setSelectedMeasurementUnits(existingUnits.map((mu) => mu.name));

    if (foodstuff.languageCode) {
      const lang = stringToLanguageCode(foodstuff.languageCode);
      if (lang) {
        setSelectedLanguage(lang);
      }
    } else if (userLanguages.length > 0) {
      setSelectedLanguage(userLanguages[0]);
    }
  }, [foodstuff, userLanguages]);

  const totalMacros = useMemo(() => {
    let protein = parseFloat(proteinInput || '0') / 100;
    let carbs = parseFloat(carbohydrateInput || '0') / 100;
    let fat = parseFloat(fatInput || '0') / 100;
    let fibre = parseFloat(fibreInput || '0') / 100;
    if (baseMeasurementUnit === 'VOLUME') {
      const densityValue = parseFloat(densityInput || '0');
      if (densityValue > 0) {
        protein /= densityValue;
        carbs /= densityValue;
        fat /= densityValue;
        fibre /= densityValue;
      }
    }
    return protein + carbs + fat + fibre;
  }, [proteinInput, carbohydrateInput, fatInput, fibreInput, baseMeasurementUnit, densityInput]);

  const macroError = totalMacros > 1
    ? t(
      'addFoodstuff_invalidMacros',
      'The sum of macro fractions exceeds 100%. Please verify your input values.'
    )
    : '';

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNameInput(e.target.value);
  }, []);

  const handleDecimalChange = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (isValidDecimalInput(value)) {
          setter(value);
        }
      },
    []
  );

  const handleMeasurementUnitChange = useCallback((event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setSelectedMeasurementUnits(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleBaseUnitChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setBaseMeasurementUnit(e.target.value as 'MASS' | 'VOLUME');
  }, []);

  const handleLanguageChange = useCallback(
    (lang: LanguageCode) => {
      setSelectedLanguage(lang);
      dispatch(patchFoodstuff({ languageCode: globalLanguageCodeToFoodstuff(lang) }));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    let energyInKcal = parseFloat(energyInput || '0') / 100;
    let proteinMassFraction = parseFloat(proteinInput || '0') / 100;
    let carbohydrateMassFraction = parseFloat(carbohydrateInput || '0') / 100;
    let fatMassFraction = parseFloat(fatInput || '0') / 100;
    let fibreMassFraction = parseFloat(fibreInput || '0') / 100;

    if (baseMeasurementUnit === 'VOLUME') {
      const densityValue = parseFloat(densityInput || '0');
      if (densityValue > 0) {
        energyInKcal /= densityValue;
        proteinMassFraction /= densityValue;
        carbohydrateMassFraction /= densityValue;
        fatMassFraction /= densityValue;
        fibreMassFraction /= densityValue;
      }
    }

    const total = proteinMassFraction + carbohydrateMassFraction + fatMassFraction + fibreMassFraction;
    if (total > 1) {
      return;
    }

    const densities: Density[] = [];
    if (baseMeasurementUnit === 'VOLUME') {
      const densityValue = parseFloat(densityInput || '0');
      densities.push({ type: 'VOLUME', value: densityValue });
    }

    const usedMeasurementUnits: MeasurementUnit[] = measurementUnits
      .filter((mu) => selectedMeasurementUnits.includes(mu.name))
      .map((mu) => ({ ...mu }));

    dispatch(
      saveFoodstuff({
        name: nameInput.trim(),
        languageCode: globalLanguageCodeToFoodstuff(selectedLanguage),
        energyInKcal,
        proteinMassFraction,
        carbohydrateMassFraction,
        fatMassFraction,
        fibreMassFraction,
        densities,
        measurementUnits: usedMeasurementUnits,
      })
    );
  }, [
    dispatch,
    energyInput,
    proteinInput,
    carbohydrateInput,
    fatInput,
    fibreInput,
    densityInput,
    baseMeasurementUnit,
    measurementUnits,
    selectedMeasurementUnits,
    nameInput,
    selectedLanguage,
  ]);

  return (
    <Card sx={{ minWidth: 275, margin: 5, padding: 3, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {t('addFoodstuff_addFoodstuff')}
        </Typography>
        <Box
          component="form"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <TextField
            margin="normal"
            id="name"
            label={t('addFoodstuff_name')}
            name="name"
            value={nameInput}
            onChange={handleNameChange}
          />

          <LanguageSelector
            languages={userLanguages}
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguageChange}
          />

          <FormControl component="fieldset">
            <RadioGroup
              row
              value={baseMeasurementUnit}
              aria-labelledby="base-measurement-unit-label"
              name="base-measurement-unit"
              onChange={handleBaseUnitChange}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="MASS"
                control={<Radio />}
                label="kcal / 100g"
              />
              <FormControlLabel
                value="VOLUME"
                control={<Radio />}
                label="kcal / 100ml"
              />
            </RadioGroup>
          </FormControl>

          {baseMeasurementUnit === 'VOLUME' && (
            <TextField
              margin="normal"
              id="density"
              label={t('addFoodstuff_density')}
              name="density"
              value={densityInput}
              onChange={handleDecimalChange(setDensityInput)}
            />
          )}

          <TextField
            margin="normal"
            id="energy"
            label={`${t('addFoodstuff_energy')} [kcal / 100 ${baseUnitSymbol}]`}
            name="energy"
            value={energyInput}
            onChange={handleDecimalChange(setEnergyInput)}
          />

          <TextField
            margin="normal"
            id="protein"
            label={`${t('addFoodstuff_protein')} [g / 100 ${baseUnitSymbol}]`}
            name="protein"
            value={proteinInput}
            onChange={handleDecimalChange(setProteinInput)}
          />

          <TextField
            margin="normal"
            id="carbohydrate"
            label={`${t('addFoodstuff_carbohydrate')} [g / 100 ${baseUnitSymbol}]`}
            name="carbohydrate"
            value={carbohydrateInput}
            onChange={handleDecimalChange(setCarbohydrateInput)}
          />

          <TextField
            margin="normal"
            id="fat"
            label={`${t('addFoodstuff_fat')} [g / 100 ${baseUnitSymbol}]`}
            name="fat"
            value={fatInput}
            onChange={handleDecimalChange(setFatInput)}
          />

          <TextField
            margin="normal"
            id="fibre"
            label={`${t('addFoodstuff_fiber')} [g / 100 ${baseUnitSymbol}]`}
            name="fibre"
            value={fibreInput}
            onChange={handleDecimalChange(setFibreInput)}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="measurement-units-label">
              {t('addFoodstuff_measurementUnits')}
            </InputLabel>
            <Select
              labelId="measurement-units-label"
              id="measurement-units"
              multiple
              value={selectedMeasurementUnits}
              onChange={handleMeasurementUnitChange}
              input={<OutlinedInput label={t('addFoodstuff_measurementUnits')} />}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={MenuProps}
            >
              {measurementUnits.map((mu) => (
                <MenuItem key={mu.name} value={mu.name}>
                  <Checkbox checked={selectedMeasurementUnits.indexOf(mu.name) > -1} />
                  <ListItemText primary={mu.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {macroError && (
            <Typography variant="body2" color="error">
              {macroError}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
            onClick={handleSubmit}
            disabled={Boolean(macroError)}
          >
            {t('addFoodstuff_add')} {nameInput || ''}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
